import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { NOTIFICATION_CHANNEL } from '@his/shared';
import { Contact, Send } from 'lucide-react';
import { post } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { Alert, Field, Input, Textarea } from '@/components/ui/primitives';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { useToast } from '@/components/ui/toast';
import { safeReturnTo } from '../opd/BookAppointmentPage';
import { CHANNEL_LABELS } from './types';

export function SendMessagePage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const returnTo = safeReturnTo(params.get('returnTo'), '/notifications');
  const [error, setError] = useState<string | null>(null);
  const [initial] = useState({ channel: 'sms', to: '', subject: '', body: '' });
  const [form, setForm] = useState(initial);

  const send = useMutation({
    mutationFn: () =>
      post('/notifications/send', {
        channel: form.channel,
        to: form.to,
        subject: form.subject || undefined,
        body: form.body,
      }),
    onSuccess: () => {
      toast.success('Queued', 'It will show in the list as it goes out');
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
      navigate(returnTo, { replace: true });
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const ready = form.to.trim().length >= 3 && form.body.trim().length >= 1;
  const dirty = !send.isSuccess && JSON.stringify(form) !== JSON.stringify(initial);

  return (
    <FormScreen
      title="Send a message"
      description="One-off, to a single number or address"
      icon={Send}
      backTo={returnTo}
      dirty={dirty}
      onSubmit={(event) => {
        event.preventDefault();
        if (!ready) return;
        setError(null);
        send.mutate();
      }}
    >
      <FormSection title={tr('admin', 'contact')} icon={Contact}>
        <Field label="Channel" required>
          <ChoiceButtons
            layout="chips"
            value={form.channel}
            onChange={(channel) => setForm({ ...form, channel })}
            required
            options={NOTIFICATION_CHANNEL.map((channel) => ({ value: channel, label: CHANNEL_LABELS[channel] ?? channel }))}
          />
        </Field>
        <Field label={form.channel === 'email' ? 'Email address' : 'Mobile number'} required>
          <Input value={form.to} onChange={(e) => setForm({ ...form, to: e.target.value })} />
        </Field>
      </FormSection>

      <FormSection title={tr('admin', 'message')} icon={Send} columns={1}>
        {form.channel === 'email' && (
          <Field label="Subject">
            <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} />
          </Field>
        )}
        <Field label="Message" required>
          <Textarea rows={4} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} />
        </Field>
      </FormSection>

      {error && <Alert variant="destructive">{error}</Alert>}

      <FormActions submitLabel="Send" loading={send.isPending} incomplete={!ready} cancelTo={returnTo} />
    </FormScreen>
  );
}
